import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Upload, KeyRound, AlertTriangle, Check, FileText, Users, Briefcase, FileCheck, PoundSterling, Truck, Plug, Zap, UserPlus, Trash2, RotateCcw, Activity, Bell, Mail, ScanSearch, Key, CheckCircle2 } from "lucide-react";
import { useConfirm } from "@/lib/confirm";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Card, PageHeader, Btn, Input, Label, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { downloadFromUrl, downloadOrOpenBlob } from "@/lib/download";
import { formatApiError } from "@/lib/format";

const EXPORTS = [
  { key: "clients", label: "Clients", icon: Users, file: "clients.csv" },
  { key: "suppliers", label: "Suppliers", icon: Truck, file: "suppliers.csv" },
  { key: "projects", label: "Projects", icon: Briefcase, file: "projects.csv" },
  { key: "quotes", label: "Quotes", icon: FileText, file: "quotes.csv" },
  { key: "proofing", label: "Proofing", icon: FileCheck, file: "proofing.csv" },
  { key: "invoiced", label: "Invoiced", icon: PoundSterling, file: "invoiced.csv" },
];

const IMPORTS = [
  {
    key: "clients",
    label: "Clients",
    headers: ["name", "email", "phone", "address"],
    required: ["name"],
    notes: "Required: name. Optional: email, phone, address. Existing clients (matched by name, case-insensitive) are skipped.",
  },
  {
    key: "contacts",
    label: "Contacts",
    headers: ["client_name", "name", "email", "phone"],
    required: ["client_name", "name"],
    notes: "Required: client_name (must match an existing client), name. Adds the contact to that client. Contacts already on the client (matched by name, case-insensitive) are skipped — import clients first.",
  },
  {
    key: "projects",
    label: "Projects",
    headers: ["client_name", "name", "job_number", "status", "start_date", "end_date", "budget"],
    required: ["client_name", "name"],
    notes: "Required: client_name (must match an existing client), name. Optional: job_number (auto if blank), status (Not Started / In Progress / Completed / On Hold / Invoiced), start_date / end_date as YYYY-MM-DD, budget.",
  },
  {
    key: "project-tasks",
    label: "Project tasks",
    headers: ["job_number", "name", "description", "task_type", "cost_type", "cost", "cost_min", "cost_max", "supplier_cost", "status", "deadline", "date_started", "notes"],
    required: ["job_number", "name"],
    notes: "Required: job_number (must match an existing project), name. Optional: task_type (Design / Print / blank), cost_type (single / range), cost (when single), cost_min + cost_max (when range), supplier_cost (Print only), status (Not Started / In Progress / Completed / On Hold), description, notes, dates as YYYY-MM-DD.",
  },
  {
    key: "quotes",
    label: "Quotes",
    headers: ["client_name", "contact_name", "name", "quote_number", "status", "date"],
    required: ["client_name", "name"],
    notes: "Required: client_name (must exist), name. Optional: contact_name (must match a contact under the client), quote_number (auto if blank), status (Draft / Sent / Accepted / Rejected), date as YYYY-MM-DD.",
  },
  {
    key: "quote-tasks",
    label: "Quote tasks",
    headers: ["quote_number", "name", "description", "task_type", "cost_type", "cost", "cost_min", "cost_max", "supplier_cost", "status", "deadline", "date_started", "notes"],
    required: ["quote_number", "name"],
    notes: "Required: quote_number (must match an existing quote), name. Same task fields as project tasks.",
  },
  {
    key: "tasks",
    label: "Tasks (combined — project & quote)",
    headers: ["id", "project_id", "quote_id", "name", "description", "date_started", "deadline", "status", "cost", "notes", "position"],
    required: ["name"],
    notes: "Use this for raw exports that mix project tasks and quote tasks in one file. Each row routes to project_id (looked up via projects.external_id) or quote_id (via quotes.external_id). Rows with both go to the project. Respects 'position' for order. Recommended for the 5-CSV import flow.",
  },
];

const HealthRow = ({ label, result, extra, testId, tone }) => {
  const ok = result?.ok;
  const pending = !result;
  const isInfo = !pending && !ok && tone === "info";
  const colour = pending
    ? "bg-zinc-100 text-zinc-600 border-zinc-200"
    : ok
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : isInfo
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-red-50 text-red-800 border-red-200";
  const dot = pending ? "bg-zinc-400" : ok ? "bg-emerald-500" : isInfo ? "bg-amber-500" : "bg-red-500";
  const status = pending ? "Pending" : ok ? "OK" : isInfo ? "Expected" : "Failed";
  return (
    <li className="flex items-start justify-between gap-3 flex-wrap" data-testid={testId}>
      <div className="flex items-start gap-2 min-w-0">
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot}`} />
        <div className="min-w-0">
          <div className="text-sm text-zinc-900">{label}</div>
          {extra && <div className="text-[11px] text-zinc-500">{extra}</div>}
          {!ok && !isInfo && result?.error && <div className="text-[11px] text-red-700">{result.error}</div>}
        </div>
      </div>
      <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${colour}`}>
        {status}
        {result && <span className="text-zinc-500">· {result.ms}ms</span>}
      </span>
    </li>
  );
};

const Admin = () => {
  const confirm = useConfirm();
  const { user: currentUser, refresh: refreshAuth } = useAuth();
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwResult, setPwResult] = useState(null);
  const [nameForm, setNameForm] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameResult, setNameResult] = useState(null);
  const [importing, setImporting] = useState({});
  const [importResults, setImportResults] = useState({});
  const fileRefs = useRef({});

  // Users management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [userResult, setUserResult] = useState(null);

  // Notification preferences (email alerts when a client submits a proof decision)
  const [notifPrefs, setNotifPrefs] = useState({ loading: true, notify_proof_emails: true, mailer_configured: false, email: "" });
  const [notifSaving, setNotifSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // OpenAI API key — used by the Visual Check tool. Stored encrypted-at-rest
  // server-side; we only ever see the masked preview after save.
  const [openaiKey, setOpenaiKey] = useState({ loading: true, configured: false, masked: "" });
  const [openaiEditing, setOpenaiEditing] = useState(false);
  const [openaiInput, setOpenaiInput] = useState("");
  const [openaiBusy, setOpenaiBusy] = useState(false);

  const loadOpenaiKey = async () => {
    try {
      const { data } = await api.get("/admin/settings/openai-key");
      setOpenaiKey({ loading: false, ...data });
    } catch {
      setOpenaiKey({ loading: false, configured: false, masked: "" });
    }
  };

  const saveOpenaiKey = async () => {
    const key = openaiInput.trim();
    if (!key) return;
    setOpenaiBusy(true);
    try {
      const { data } = await api.put("/admin/settings/openai-key", { api_key: key });
      toast.success("OpenAI key saved", { description: `Stored as ${data.masked}.` });
      setOpenaiKey({ loading: false, ...data });
      setOpenaiInput("");
      setOpenaiEditing(false);
    } catch (err) {
      toast.error("Couldn't save key", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setOpenaiBusy(false);
    }
  };

  const clearOpenaiKey = async () => {
    const ok = await confirm({
      title: "Remove OpenAI API key?",
      description: "Visual Check won't run again until a new key is saved.",
      confirmText: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    setOpenaiBusy(true);
    try {
      await api.put("/admin/settings/openai-key", { api_key: "" });
      toast.success("OpenAI key removed.");
      setOpenaiKey({ loading: false, configured: false, masked: "" });
    } catch (err) {
      toast.error("Couldn't remove key", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setOpenaiBusy(false);
    }
  };

  const loadNotifPrefs = async () => {
    try {
      const { data } = await api.get("/auth/notification-prefs");
      setNotifPrefs({ loading: false, ...data });
    } catch {
      setNotifPrefs((p) => ({ ...p, loading: false }));
    }
  };

  const toggleProofNotifications = async (value) => {
    setNotifSaving(true);
    try {
      await api.post("/auth/notification-prefs", { notify_proof_emails: value });
      setNotifPrefs((p) => ({ ...p, notify_proof_emails: value }));
      toast.success(value ? "You'll get email alerts on proof decisions." : "Proof-decision email alerts turned off.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNotifSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/auth/send-test-email");
      toast.success("Test email sent", { description: `Resend accepted it for ${data.to}. Check your inbox (and spam folder).` });
    } catch (err) {
      toast.error("Test email failed", { description: formatApiError(err) });
    } finally {
      setTestingEmail(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data || []);
    } catch (err) {
      setUserResult({ ok: false, msg: formatApiError(err) });
    } finally {
      setUsersLoading(false);
    }
  };

  const [xeroStatus, setXeroStatus] = useState({ loading: true });
  const [xeroBusy, setXeroBusy] = useState(false);
  const [xeroToast, setXeroToast] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // System health — verifies backend reachability + auth roundtrip from
  // whichever origin the app is currently loaded on. Runs three checks:
  //   1. Backend reachable (no auth) — CORS preflight + plain GET /api/health
  //   2. Cookie roundtrip — GET /api/auth/me with credentials only
  //   3. Bearer token — GET /api/auth/me with Authorization header from localStorage
  // When the frontend origin and backend host differ (cross-domain), browsers
  // typically block the third-party cookie — that's expected and the app falls
  // back to the bearer token automatically. So the card is "healthy" when
  // reach && (cookie || bearer) succeed.
  const [health, setHealth] = useState({ loading: true });
  const runHealthCheck = async () => {
    setHealth((h) => ({ ...h, loading: true }));
    const origin = typeof window !== "undefined" ? window.location.origin : "(unknown)";
    const backend = API.replace(/\/api$/, "");
    let crossDomain = false;
    try {
      crossDomain = new URL(origin).host !== new URL(backend).host;
    } catch { /* ignore parse errors */ }
    const token = (typeof window !== "undefined") ? (localStorage.getItem("kerboom_token") || null) : null;

    const time = async (fn) => {
      const t0 = performance.now();
      try {
        const out = await fn();
        return { ...out, ms: Math.round(performance.now() - t0) };
      } catch (err) {
        return { ok: false, status: 0, error: err?.message || "Network error", ms: Math.round(performance.now() - t0) };
      }
    };

    const [reach, cookie, bearer] = await Promise.all([
      time(async () => {
        const r = await fetch(`${API}/health`, { method: "GET", credentials: "include" });
        return { ok: r.ok, status: r.status, error: r.ok ? null : `HTTP ${r.status}` };
      }),
      time(async () => {
        const r = await fetch(`${API}/auth/me`, { method: "GET", credentials: "include" });
        if (r.ok) {
          const body = await r.json();
          return { ok: true, status: r.status, name: body?.email || body?.name || null, error: null };
        }
        return { ok: false, status: r.status, name: null, error: `HTTP ${r.status}` };
      }),
      time(async () => {
        if (!token) return { ok: false, status: 0, name: null, error: "No token in localStorage" };
        const r = await fetch(`${API}/auth/me`, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const body = await r.json();
          return { ok: true, status: r.status, name: body?.email || body?.name || null, error: null };
        }
        return { ok: false, status: r.status, name: null, error: `HTTP ${r.status}` };
      }),
    ]);

    setHealth({ loading: false, origin, backend, crossDomain, hasToken: !!token, reach, cookie, bearer, checked_at: new Date() });
  };

  const loadXeroStatus = async () => {
    try {
      const { data } = await api.get("/xero/status");
      setXeroStatus({ loading: false, ...data });
    } catch (err) {
      setXeroStatus({ loading: false, configured: false, connected: false, error: formatApiError(err) });
    }
  };

  useEffect(() => {
    loadXeroStatus();
    loadUsers();
    runHealthCheck();
    loadNotifPrefs();
    loadOpenaiKey();
    if (currentUser?.name) setNameForm(currentUser.name);
    if (searchParams.get("xero_connected")) {
      setXeroToast({ ok: true, msg: "Xero connected successfully." });
      searchParams.delete("xero_connected");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("xero_error")) {
      setXeroToast({ ok: false, msg: `Xero connection failed: ${searchParams.get("xero_error")}` });
      searchParams.delete("xero_error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectXero = async () => {
    setXeroBusy(true);
    try {
      const { data } = await api.get("/xero/connect");
      // Top-level navigation to Xero — must be window.top so the iframe doesn't block
      try { window.top.location.href = data.authorize_url; }
      catch { window.location.href = data.authorize_url; }
    } catch (err) {
      setXeroToast({ ok: false, msg: formatApiError(err) });
      setXeroBusy(false);
    }
  };

  const disconnectXero = async () => {
    setXeroBusy(true);
    try {
      await api.post("/xero/disconnect");
      setXeroToast({ ok: true, msg: "Xero disconnected." });
      await loadXeroStatus();
    } catch (err) {
      setXeroToast({ ok: false, msg: formatApiError(err) });
    } finally {
      setXeroBusy(false);
    }
  };

  const submitNewUser = async (e) => {
    e.preventDefault();
    setUserResult(null);
    const email = newUser.email.trim().toLowerCase();
    if (!email) {
      setUserResult({ ok: false, msg: "Email is required" });
      return;
    }
    if (newUser.password.length < 8) {
      setUserResult({ ok: false, msg: "Password must be at least 8 characters" });
      return;
    }
    setCreatingUser(true);
    try {
      await api.post("/auth/users", {
        email,
        name: newUser.name.trim(),
        password: newUser.password,
      });
      setUserResult({ ok: true, msg: `User ${email} created.` });
      setNewUser({ email: "", name: "", password: "" });
      await loadUsers();
    } catch (err) {
      setUserResult({ ok: false, msg: formatApiError(err) });
    } finally {
      setCreatingUser(false);
    }
  };

  const removeUser = async (u) => {
    const ok = await confirm({
      title: "Delete user?",
      description: `Are you sure you want to delete ${u.email}? They'll lose access immediately.`,
      confirmText: "Delete user",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      setUserResult({ ok: true, msg: `User ${u.email} deleted.` });
      await loadUsers();
    } catch (err) {
      setUserResult({ ok: false, msg: formatApiError(err) });
    }
  };

  const resetUserPassword = async (u) => {
    const pwd = window.prompt(`Set a new password for ${u.email}.\n\nMin 8 characters. Tell them this password — they should change it after their next login.`);
    if (pwd === null) return;
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await api.post(`/auth/users/${u.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${u.email}`, { description: "Share the new password with them securely." });
      setUserResult({ ok: true, msg: `Password reset for ${u.email}.` });
    } catch (err) {
      setUserResult({ ok: false, msg: formatApiError(err) });
      toast.error("Reset failed", { description: formatApiError(err) });
    }
  };

  const submitName = async (e) => {
    e.preventDefault();
    setNameResult(null);
    const name = nameForm.trim();
    if (!name) {
      setNameResult({ ok: false, msg: "Display name cannot be empty" });
      return;
    }
    setNameSaving(true);
    try {
      await api.post("/auth/update-profile", { name });
      setNameResult({ ok: true, msg: "Display name updated." });
      if (refreshAuth) await refreshAuth();
    } catch (err) {
      setNameResult({ ok: false, msg: formatApiError(err) });
    } finally {
      setNameSaving(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwResult(null);
    if (pwForm.new_password !== pwForm.confirm) {
      setPwResult({ ok: false, msg: "New password and confirmation do not match" });
      return;
    }
    if (pwForm.new_password.length < 8) {
      setPwResult({ ok: false, msg: "New password must be at least 8 characters" });
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwResult({ ok: true, msg: "Password updated successfully." });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwResult({ ok: false, msg: formatApiError(err) });
    } finally {
      setPwSaving(false);
    }
  };

  const exportCsv = (key) => {
    downloadFromUrl(`/admin/export/${key}.csv`);
  };

  const downloadTemplate = (entity) => {
    const csv = entity.headers.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    downloadOrOpenBlob(blob, `${entity.key}-template.csv`);
  };

  const onImportFile = async (entity, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting((s) => ({ ...s, [entity.key]: true }));
    setImportResults((s) => ({ ...s, [entity.key]: null }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/import/${entity.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResults((s) => ({ ...s, [entity.key]: { ok: true, ...data } }));
    } catch (err) {
      setImportResults((s) => ({ ...s, [entity.key]: { ok: false, error: formatApiError(err) } }));
    } finally {
      setImporting((s) => ({ ...s, [entity.key]: false }));
      e.target.value = "";
    }
  };

  return (
    <Shell>
      <PageHeader
        testId="admin-header"
        title="Admin"
        subtitle="Account, data import & export"
      />

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Xero Integration */}
        <Card className="p-6 lg:col-span-2" data-testid="admin-xero-card">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-50 grid place-items-center"><Plug className="w-5 h-5 text-sky-700" /></div>
              <div>
                <h2 className="font-display text-xl font-bold text-zinc-950">Xero</h2>
                <p className="text-xs text-zinc-500">Connect your Xero org so you can push completed projects as draft invoices.</p>
              </div>
            </div>
            {xeroStatus.loading ? (
              <Spinner />
            ) : xeroStatus.connected ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span data-testid="xero-status-connected" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Connected · {xeroStatus.tenant_name || "(unnamed org)"}
                </span>
                <Btn size="sm" variant="secondary" onClick={disconnectXero} disabled={xeroBusy} data-testid="xero-disconnect-btn">
                  {xeroBusy ? "…" : "Disconnect"}
                </Btn>
              </div>
            ) : (
              <Btn onClick={connectXero} disabled={xeroBusy || !xeroStatus.configured} data-testid="xero-connect-btn">
                <Zap className="w-3.5 h-3.5" />{xeroBusy ? "Redirecting…" : "Connect to Xero"}
              </Btn>
            )}
          </div>
          {!xeroStatus.loading && !xeroStatus.configured && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
              Xero credentials not yet configured on the server.
            </p>
          )}
          {xeroToast && (
            <p className={`text-sm inline-flex items-center gap-1.5 mt-3 ${xeroToast.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="xero-toast">
              {xeroToast.ok ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {xeroToast.msg}
            </p>
          )}
          {xeroStatus.connected && (
            <p className="text-[11px] text-zinc-500 mt-3">
              Each Kerboom client must be linked to a Xero contact (open the client &amp; pick from the list). Then on a Project, mark the line items as <strong>Client Chosen</strong> and click <strong>Send to Xero</strong> — a draft invoice is created with one line per chosen task.
            </p>
          )}
        </Card>

        {/* System Health — verifies backend reachability + cookie roundtrip from this origin */}
        <Card className="p-6 lg:col-span-2" data-testid="admin-health-card">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center"><Activity className="w-5 h-5 text-emerald-700" /></div>
              <div>
                <h2 className="font-display text-xl font-bold text-zinc-950">System Health</h2>
                <p className="text-xs text-zinc-500">One-click check from this origin — confirms the backend is reachable and the login cookie is round-tripping.</p>
              </div>
            </div>
            <Btn size="sm" variant="secondary" onClick={runHealthCheck} disabled={health.loading} data-testid="admin-health-recheck">
              <RotateCcw className="w-3.5 h-3.5" />{health.loading ? "Checking…" : "Recheck"}
            </Btn>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4 text-xs">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid="admin-health-origin">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">This origin (browser)</div>
              <div className="font-mono text-zinc-900 break-all">{health.origin || "—"}</div>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid="admin-health-backend">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">Backend URL</div>
              <div className="font-mono text-zinc-900 break-all">{health.backend || "—"}</div>
            </div>
          </div>

          {health.loading && !health.reach ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500" data-testid="admin-health-loading"><Spinner /> Running checks…</div>
          ) : (
            <ul className="space-y-2">
              <HealthRow
                label="Backend reachable (CORS preflight + GET /api/health)"
                result={health.reach}
                testId="admin-health-reach"
              />
              <HealthRow
                label="Cookie roundtrip (GET /api/auth/me with credentials)"
                result={health.cookie}
                extra={health.cookie?.ok && health.cookie?.name ? `signed in as ${health.cookie.name}` : (health.crossDomain && !health.cookie?.ok ? "Expected — browsers block third-party cookies across domains. Auth falls back to the bearer token below." : null)}
                tone={health.crossDomain && !health.cookie?.ok ? "info" : null}
                testId="admin-health-cookie"
              />
              <HealthRow
                label="Bearer token (Authorization header from localStorage)"
                result={health.bearer}
                extra={health.bearer?.ok && health.bearer?.name ? `signed in as ${health.bearer.name}` : (!health.hasToken ? "No kerboom_token in localStorage — sign in again." : null)}
                testId="admin-health-bearer"
              />
            </ul>
          )}

          {!health.loading && health.reach?.ok && !health.cookie?.ok && !health.bearer?.ok && (
            <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3" data-testid="admin-health-hint">
              Both auth methods failed. Sign out and sign back in. If this persists right after a deploy, the JWT secret on the server may have rotated and invalidated existing tokens.
            </p>
          )}
          {!health.loading && health.reach?.ok && (health.bearer?.ok || health.cookie?.ok) && health.crossDomain && !health.cookie?.ok && (
            <p className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 mt-3" data-testid="admin-health-hint">
              <strong>All good.</strong> Frontend (<code>{new URL(health.origin).host}</code>) and backend (<code>{new URL(health.backend).host}</code>) are on different domains, so the browser blocks the third-party cookie — the bearer token in <code>localStorage</code> carries auth instead. To enable cookies too, host the backend on a subdomain like <code>api.kerboom.design</code>.
            </p>
          )}

          {!health.loading && health.checked_at && (
            <p className="text-[11px] text-zinc-500 mt-3" data-testid="admin-health-timestamp">
              Last checked {health.checked_at.toLocaleTimeString()}
            </p>
          )}
        </Card>

        {/* Account / Password */}
        <Card className="p-6 lg:col-span-2" data-testid="admin-account-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-zinc-100 grid place-items-center"><KeyRound className="w-5 h-5 text-zinc-700" /></div>
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-950">Account</h2>
              <p className="text-xs text-zinc-500">Display name &amp; password</p>
            </div>
          </div>

          <form onSubmit={submitName} className="grid sm:grid-cols-3 gap-4 max-w-3xl mb-6 pb-6 border-b border-zinc-100" data-testid="admin-name-form">
            <div className="sm:col-span-2">
              <Label required>Display name</Label>
              <Input
                type="text"
                required
                value={nameForm}
                onChange={(e) => setNameForm(e.target.value)}
                placeholder="Your full name"
                data-testid="admin-display-name"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Shown in the sidebar and on client-facing proof pages ("Sent by …").</p>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <Btn type="submit" disabled={nameSaving} data-testid="admin-name-save">
                {nameSaving ? "Saving…" : "Update name"}
              </Btn>
            </div>
            {nameResult && (
              <div className="sm:col-span-3 -mt-2">
                <p className={`text-sm inline-flex items-center gap-1.5 ${nameResult.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="admin-name-result">
                  {nameResult.ok ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {nameResult.msg}
                </p>
              </div>
            )}
          </form>

          <form onSubmit={submitPassword} className="grid sm:grid-cols-3 gap-4 max-w-3xl" data-testid="admin-password-form">
            <div>
              <Label required>Current password</Label>
              <Input
                type="password"
                required
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                data-testid="admin-current-password"
              />
            </div>
            <div>
              <Label required>New password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                data-testid="admin-new-password"
              />
            </div>
            <div>
              <Label required>Confirm new password</Label>
              <Input
                type="password"
                required
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                data-testid="admin-confirm-password"
              />
            </div>
            <div className="sm:col-span-3 flex items-center gap-3 flex-wrap">
              <Btn type="submit" disabled={pwSaving} data-testid="admin-password-save">
                {pwSaving ? "Saving…" : "Update password"}
              </Btn>
              {pwResult && (
                <p className={`text-sm inline-flex items-center gap-1.5 ${pwResult.ok ? "text-emerald-700" : "text-red-700"}`}>
                  {pwResult.ok ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {pwResult.msg}
                </p>
              )}
              <p className="text-xs text-zinc-500">Min 8 characters.</p>
            </div>
          </form>
        </Card>

        {/* Notifications — email alerts when a client submits a proof decision */}
        <Card className="p-6 lg:col-span-2" data-testid="admin-notifications-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 grid place-items-center"><Bell className="w-5 h-5 text-amber-700" /></div>
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-950">Notifications</h2>
              <p className="text-xs text-zinc-500">Get an email the moment a client approves or requests changes on a proof.</p>
            </div>
          </div>

          {notifPrefs.loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500"><Spinner /> Loading preferences…</div>
          ) : (
            <div className="max-w-3xl space-y-4">
              {!notifPrefs.mailer_configured && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="admin-notif-mailer-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>Email sending isn't configured on the server yet.</strong>
                    <p className="mt-1 text-amber-800">Ask Paul to set <code className="bg-white/60 px-1 rounded">RESEND_API_KEY</code> in the backend <code className="bg-white/60 px-1 rounded">.env</code> and restart. Until then, the toggle below has no effect.</p>
                  </div>
                </div>
              )}

              <div className="flex items-start justify-between gap-4 p-4 border border-zinc-200 rounded-xl">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-zinc-950">Email me when a client submits a proof decision</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    You'll get a short email with the proof name, client, decision (Approved / Changes Requested),
                    feedback count, and a one-click link straight to that proof.
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-1">Sends to <strong className="text-zinc-600">{notifPrefs.email}</strong>.</p>
                </div>
                <label className="inline-flex items-center cursor-pointer shrink-0" data-testid="admin-notif-proof-toggle-wrap">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!notifPrefs.notify_proof_emails}
                    disabled={notifSaving || !notifPrefs.mailer_configured}
                    onChange={(e) => toggleProofNotifications(e.target.checked)}
                    data-testid="admin-notif-proof-toggle"
                  />
                  <span className="relative w-11 h-6 bg-zinc-200 rounded-full transition-colors peer-checked:bg-emerald-500 peer-disabled:opacity-50">
                    <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Btn
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={sendTestEmail}
                  disabled={testingEmail || !notifPrefs.mailer_configured}
                  data-testid="admin-notif-send-test"
                >
                  <Mail className="w-3.5 h-3.5" />{testingEmail ? "Sending…" : "Send test email"}
                </Btn>
                <p className="text-[11px] text-zinc-500">
                  Sends a sample alert to your address ({notifPrefs.email}). Useful to confirm the wiring after a deploy.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Visual Check — OpenAI API key for the artwork visual check tool */}
        <Card className="p-6 lg:col-span-2" data-testid="admin-visual-check-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 grid place-items-center"><ScanSearch className="w-5 h-5 text-white" /></div>
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-950">Visual Check settings</h2>
              <p className="text-xs text-zinc-500">OpenAI API key used by the Visual Check tool (button on the Proofing page).</p>
            </div>
          </div>

          {openaiKey.loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500"><Spinner /> Loading…</div>
          ) : (
            <div className="max-w-3xl space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-zinc-900 text-white shrink-0">
                    <Key className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm font-bold text-zinc-950">OpenAI API key</p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Get a key from{" "}
                      <a className="text-blue-700 underline" href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com</a>{" "}
                      (your <em>API account</em> billing — ChatGPT Plus doesn&apos;t include API access). Stored encrypted-at-rest; never shown back to you.
                    </p>
                    {openaiKey.configured && !openaiEditing && (
                      <p className="text-sm text-emerald-700 font-medium mt-2 inline-flex items-center gap-1.5" data-testid="admin-openai-key-status">
                        <CheckCircle2 className="w-4 h-4" />Configured · <code className="text-xs bg-white border border-zinc-200 px-1.5 py-0.5 rounded">{openaiKey.masked}</code>
                      </p>
                    )}
                    {!openaiKey.configured && !openaiEditing && (
                      <p className="text-sm text-amber-700 font-medium mt-2 inline-flex items-center gap-1.5" data-testid="admin-openai-key-status-empty">
                        <AlertTriangle className="w-4 h-4" />No key on file — Visual Check can&apos;t run yet.
                      </p>
                    )}
                    {openaiEditing && (
                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <input
                          type="password"
                          placeholder="sk-..."
                          value={openaiInput}
                          onChange={(e) => setOpenaiInput(e.target.value)}
                          disabled={openaiBusy}
                          className="flex-1 text-sm border border-zinc-300 rounded-md px-3 py-2"
                          data-testid="admin-openai-key-input"
                          autoFocus
                        />
                        <Btn size="sm" onClick={saveOpenaiKey} disabled={openaiBusy || !openaiInput.trim()} data-testid="admin-openai-key-save">
                          {openaiBusy ? "Saving…" : "Save"}
                        </Btn>
                        <button type="button" onClick={() => { setOpenaiEditing(false); setOpenaiInput(""); }} className="px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-200 rounded-full">Cancel</button>
                      </div>
                    )}
                  </div>
                  {!openaiEditing && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setOpenaiEditing(true)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-800"
                        data-testid="admin-openai-key-edit"
                      >
                        {openaiKey.configured ? "Replace key" : "Add key"}
                      </button>
                      {openaiKey.configured && (
                        <button
                          type="button"
                          onClick={clearOpenaiKey}
                          disabled={openaiBusy}
                          className="text-xs font-medium px-3 py-1.5 rounded-full text-red-700 hover:bg-red-50 disabled:opacity-40"
                          data-testid="admin-openai-key-remove"
                        >Remove</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
        <Card className="p-6 lg:col-span-2" data-testid="admin-users-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-violet-50 grid place-items-center"><UserPlus className="w-5 h-5 text-violet-700" /></div>
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-950">Users</h2>
              <p className="text-xs text-zinc-500">Add another user with their own login. Anyone you add gets full access.</p>
            </div>
          </div>

          <form onSubmit={submitNewUser} className="grid sm:grid-cols-4 gap-4 max-w-4xl mb-6" data-testid="admin-new-user-form">
            <div>
              <Label required>Email</Label>
              <Input
                type="email"
                required
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="name@example.com"
                data-testid="admin-new-user-email"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Optional"
                data-testid="admin-new-user-name"
              />
            </div>
            <div>
              <Label required>Password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Min 8 characters"
                data-testid="admin-new-user-password"
              />
            </div>
            <div className="flex items-end">
              <Btn type="submit" disabled={creatingUser} data-testid="admin-new-user-submit" className="w-full">
                {creatingUser ? "Adding…" : <><UserPlus className="w-3.5 h-3.5" />Add user</>}
              </Btn>
            </div>
            {userResult && (
              <div className="sm:col-span-4">
                <p className={`text-sm inline-flex items-center gap-1.5 ${userResult.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="admin-new-user-result">
                  {userResult.ok ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {userResult.msg}
                </p>
              </div>
            )}
          </form>

          <div className="border-t border-zinc-200 pt-4">
            <p className="text-xs font-semibold text-zinc-700 mb-2">Existing users ({users.length})</p>
            {usersLoading ? (
              <Spinner />
            ) : users.length === 0 ? (
              <p className="text-sm text-zinc-500">No users yet.</p>
            ) : (
              <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-xl overflow-hidden" data-testid="admin-users-list">
                {users.map((u) => {
                  const isSelf = currentUser && u.id === currentUser.id;
                  return (
                    <li key={u.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-50" data-testid={`admin-user-row-${u.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-950 truncate">
                          {u.email}
                          {isSelf && <span className="ml-2 text-[10px] font-medium text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full">you</span>}
                        </p>
                        {u.name && <p className="text-xs text-zinc-500 truncate">{u.name}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Btn
                          size="sm"
                          variant="secondary"
                          onClick={() => resetUserPassword(u)}
                          title="Set a new password for this user"
                          data-testid={`admin-user-reset-${u.id}`}
                          className="hover:bg-amber-50 hover:text-amber-800 hover:border-amber-200"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />Reset password
                        </Btn>
                        <Btn
                          size="sm"
                          variant="secondary"
                          onClick={() => removeUser(u)}
                          disabled={isSelf}
                          title={isSelf ? "You can't delete your own account" : "Delete user"}
                          data-testid={`admin-user-delete-${u.id}`}
                          className={isSelf ? "opacity-40" : "hover:bg-red-50 hover:text-red-700 hover:border-red-200"}
                        >
                          <Trash2 className="w-3.5 h-3.5" />Delete
                        </Btn>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>

        {/* Export */}
        <Card className="p-6" data-testid="admin-export-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center"><Download className="w-5 h-5 text-emerald-700" /></div>
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-950">Export</h2>
              <p className="text-xs text-zinc-500">Download CSVs of any section.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {EXPORTS.map((ex) => (
              <button
                key={ex.key}
                onClick={() => exportCsv(ex.key, ex.file)}
                data-testid={`admin-export-${ex.key}`}
                className="flex items-center gap-3 p-3 border border-zinc-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors text-left"
              >
                <ex.icon className="w-4 h-4 text-zinc-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-950">{ex.label}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{ex.file}</p>
                </div>
                <Download className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
          </div>
        </Card>

        {/* Import */}
        <Card className="p-6" data-testid="admin-import-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 grid place-items-center"><Upload className="w-5 h-5 text-blue-700" /></div>
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-950">Import</h2>
              <p className="text-xs text-zinc-500">Bring data in from your old app via CSV.</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
            <strong>Order matters.</strong> Import <strong>Clients</strong> first — Projects and Quotes link to clients by name.
          </div>

          <div className="space-y-3">
            {IMPORTS.map((entity) => {
              const result = importResults[entity.key];
              const busy = importing[entity.key];
              return (
                <div key={entity.key} className="border border-zinc-200 rounded-xl p-4" data-testid={`admin-import-${entity.key}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-zinc-950">{entity.label}</p>
                      <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{entity.notes}</p>
                      <p className="text-[11px] text-zinc-500 mt-2">
                        <strong>Headers:</strong>{" "}
                        {entity.headers.map((h) => (
                          <span key={h} className={`inline-block mr-1 mb-1 px-1.5 py-0.5 rounded ${entity.required.includes(h) ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`}>
                            {h}{entity.required.includes(h) ? "*" : ""}
                          </span>
                        ))}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        ref={(r) => { fileRefs.current[entity.key] = r; }}
                        onChange={(e) => onImportFile(entity, e)}
                        className="hidden"
                        data-testid={`admin-import-input-${entity.key}`}
                      />
                      <Btn size="sm" variant="secondary" onClick={() => downloadTemplate(entity)} data-testid={`admin-template-${entity.key}`}>
                        <FileText className="w-3.5 h-3.5" />Template
                      </Btn>
                      <Btn size="sm" disabled={busy} onClick={() => fileRefs.current[entity.key]?.click()} data-testid={`admin-import-btn-${entity.key}`}>
                        {busy ? "Importing…" : <><Upload className="w-3.5 h-3.5" />Choose CSV</>}
                      </Btn>
                    </div>
                  </div>

                  {result && (
                    <div className={`mt-3 p-3 rounded-lg text-xs ${result.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-red-50 border border-red-200 text-red-900"}`} data-testid={`admin-import-result-${entity.key}`}>
                      {result.ok ? (
                        <>
                          <p className="font-semibold">
                            {result.created} created · {result.skipped} skipped · {result.errors?.length || 0} error{(result.errors?.length || 0) !== 1 ? "s" : ""}
                          </p>
                          {result.errors?.length > 0 && (
                            <ul className="mt-1.5 list-disc list-inside space-y-0.5 max-h-32 overflow-auto">
                              {result.errors.map((msg) => <li key={msg}>{msg}</li>)}
                            </ul>
                          )}
                        </>
                      ) : (
                        <p>Import failed: {result.error}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </Shell>
  );
};

export default Admin;
